<template>
  <!-- With a top bar the drawer starts underneath it; without one it runs the full height of the
       window. The same answer drives the palette: the dark treatment belongs to whatever sits at
       the top of the window. -->
  <div
    id="advisor-side-nav"
    :class="{
      'advisor-side-nav--below-bar': $store.getters.hasContextTabs,
    }"
    :style="{ '--advisor-top-bar-height': topBarHeight + 'px' }"
  >
    <pc-modal
      id="plan-modal"
      size="medium"
      v-if="showUpsertPlanModal"
      :show="showUpsertPlanModal"
      title=""
      @close="closeUpsertPlanModal"
      :attach="true"
      :closable="false"
    >
      <div class="modal-title">{{ planToEdit ? "Edit plan" : "Add plan" }}</div>

      <v-form class="mt-6" ref="upsertPlanForm" v-model="isUpsertPlanFormValid">
        <v-row>
          <v-col>
            <!-- use your reskinned text field if you prefer -->
            <v-text-field
              v-model.trim="planName"
              data-id="b05be0cf-e43e-4dbd-ac13-97d321a3b4e2"
              label="Plan name"
              maxlength="60"
              :color="$colors.penChecksTeal"
              :rules="[planNameRequired, planNameValid]"
              :disabled="upsertingPlan"
              autofocus
            />
          </v-col>
        </v-row>
      </v-form>

      <div class="float-right mt-8">
        <pc-button
          outlined
          :disabled="upsertingPlan"
          @click="closeUpsertPlanModal"
          id="cancelButton"
          data-id="172a1739-a3d7-4d05-ac29-b008cd6858f9"
        >
          Cancel
        </pc-button>
        <pc-button
          class="ml-4"
          :disabled="upsertingPlan || !planName"
          @click="submitUpsertPlan"
          id="submitButton"
          data-id="2bcfb93e-f0dc-4d67-86fb-72ec2a30f006"
        >
          {{ planToEdit ? "Save" : "Add" }} Plan
        </pc-button>
      </div>
    </pc-modal>
    <v-navigation-drawer
      color="white"
      floating
      :mini-variant="!sideMenuIsExpanded"
      light
      app
      permanent
    >
      <pc-plan-switcher
        ref="planSwitcher"
        v-if="
          $store.state.masterAccount && $store.state.masterAccount.isAdvisor()
        "
        :account-id="
          $store.state.masterAccount && $store.state.masterAccount.id
        "
        v-model="selectedPlanId"
        :small="!sideMenuIsExpanded"
        @add-plan="openAddPlanModal"
        @edit-plan="openEditPlanModal"
      >
      </pc-plan-switcher>
      <pc-account-switcher
        :accounts="accounts"
        redirect-path-prefix="/accounts"
        v-if="
          !showsTierSelector &&
          accounts.length &&
          $store.state.masterAccount &&
          ($store.state.masterAccount.isAdvisoryFirm() ||
            $store.state.masterAccount.isRegisteredInvestmentAdvisor())
        "
        v-model="selectedAccountId"
        :class="[
          'account-switcher',
          $store.getters.hasContextTabs
            ? 'account-switcher--secondary'
            : 'account-switcher--default',
        ]"
        :small="!sideMenuIsExpanded"
      >
      </pc-account-switcher>

      <!-- Which account is being operated as, below the anchor, where the switcher above cannot
           name it. One tag for every tier: the accounts it offers come from the tier in context. -->
      <pc-advisor-tier-account-selector
        v-if="showsTierSelector"
        :small="!sideMenuIsExpanded"
      ></pc-advisor-tier-account-selector>

      <pc-ria-left-side-navigation-menu
        v-if="
          $store.state.masterAccount &&
          $store.state.masterAccount.isRegisteredInvestmentAdvisor()
        "
        :activeAccount="$store.state.masterAccount"
        :activeItem="activeItem"
        :sideMenuIsExpanded="sideMenuIsExpanded"
      >
      </pc-ria-left-side-navigation-menu>

      <pc-advisory-firm-left-side-navigation-menu
        v-else-if="
          $store.state.masterAccount &&
          $store.state.masterAccount.isAdvisoryFirm()
        "
        :activeAccount="$store.state.masterAccount"
        :activeItem="activeItem"
        :sideMenuIsExpanded="sideMenuIsExpanded"
      >
      </pc-advisory-firm-left-side-navigation-menu>

      <pc-advisor-left-side-navigation-menu
        v-else-if="
          $store.state.masterAccount && $store.state.masterAccount.isAdvisor()
        "
        :activeAccount="$store.state.masterAccount"
        :activeItem="activeItem"
        :sideMenuIsExpanded="sideMenuIsExpanded"
      >
      </pc-advisor-left-side-navigation-menu>

      <v-btn
        @click="menuExpandArrowClicked"
        rounded
        absolute
        :color="$colors.white"
        style="bottom: 75px; right: 5px"
        small
        elevation="0"
        data-id="ccf48156-8134-4499-bc8e-c1fb9c8e698c"
        ><pc-icon
          :height="18"
          :color="$colors.penChecksDarkGray"
          :name="sideMenuIsExpanded ? 'pc-angle-left' : 'pc-angle-right'"
        ></pc-icon
      ></v-btn>
    </v-navigation-drawer>
  </div>
</template>

<script>
import PcPlanSwitcher from "@/shared/components/new-components/PcPlanSwitcher.vue";
import PcAccountSwitcher from "@/shared/components/new-components/PcAccountSwitcher.vue";
import PcRiaLeftSideNavigationMenu from "@/advisor/components/ria/PcRiaLeftSideNavigationMenu";
import PcAdvisorLeftSideNavigationMenu from "@/advisor/components/advisor/PcAdvisorLeftSideNavigationMenu";
import PcAdvisoryFirmLeftSideNavigationMenu from "@/advisor/components/advisory-firm/PcAdvisoryFirmLeftSideNavigationMenu";
import PcAdvisorTierAccountSelector from "@/advisor/components/layout/PcAdvisorTierAccountSelector";
import Account from "@/shared/lib/client-sdk/models/account";
import Plan from "@/shared/lib/client-sdk/models/plan";
import { isBelowAnchor } from "@/advisor/lib/navigation";
import { HEIGHT as TOP_BAR_HEIGHT } from "@/advisor/components/layout/PcAdvisorTopBarNavigation";

export default {
  components: {
    Account,
    PcAccountSwitcher,
    PcPlanSwitcher,
    PcRiaLeftSideNavigationMenu,
    PcAdvisorLeftSideNavigationMenu,
    PcAdvisoryFirmLeftSideNavigationMenu,
    PcAdvisorTierAccountSelector,
  },

  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
    activeItem: {
      type: String,
    },
    activeAccount: {
      type: Account,
      required: true,
    },
  },

  watch: {
    // Asked for here rather than inside the selector: the list is what decides whether the
    // selector is drawn at all, so asking from inside it would come too late.
    isBelowAnchor: {
      immediate: true,
      handler: function (isBelowAnchor) {
        if (isBelowAnchor) this.$store.dispatch("ensureReachableAccounts");
      },
    },
    collapsed: function () {
      this.sideMenuIsExpanded = !this.collapsed;
    },
    sideMenuIsExpanded: function () {
      this.$store.commit("setSideNavCollapsed", !this.sideMenuIsExpanded);
    },
  },

  methods: {
    menuExpandArrowClicked: function () {
      this.sideMenuIsExpanded = !this.sideMenuIsExpanded;
    },
    openAddPlanModal() {
      this.planToEdit = null;
      this.planName = "";
      this.showUpsertPlanModal = true;
      this.$nextTick(() => {
        // focus is handled via autofocus on v-text-field
      });
    },
    openEditPlanModal(plan) {
      this.planToEdit = new Plan(plan.id);
      this.planName = plan.displayName;
      this.showUpsertPlanModal = true;
      this.$nextTick(() => {
        // focus is handled via autofocus on v-text-field
      });
    },
    closeUpsertPlanModal() {
      if (this.upsertingPlan) return;
      this.showUpsertPlanModal = false;
      this.planName = "";
      this.planToEdit = null;
    },
    submitUpsertPlan() {
      if (!this.accountId) return;
      const ok = this.$refs.upsertPlanForm
        ? this.$refs.upsertPlanForm.validate()
        : true;
      if (!ok) return;

      if (this.planToEdit) {
        this.editPlan();
      } else {
        this.addPlan();
      }
    },
    addPlan() {
      this.upsertingPlan = true;

      Plan.createAdvisorPlanForAccount(
        this.accountId,
        this.planName,
        (plan, err) => {
          this.upsertingPlan = false;

          if (err || !plan) {
            this.$snackbar &&
              this.$snackbar.error &&
              this.$snackbar.error(err || "Failed to create plan.");
            return;
          }

          this.selectedPlanId = plan.id;

          // Persist selected plan on the account so it sticks on next login
          Plan.setSelectedPlanOnAccount(this.accountId, plan.id, () => {});

          // Redirect to the plan's home page: /accounts/{accountId}
          const targetPath = `/accounts/${this.accountId}`;
          if (this.$route.path !== targetPath) {
            this.$router.push(targetPath);
          } else {
            this.$router.go(0);
          }
          // Refresh the switcher list so the new plan appears in the menu
          this.$refs.planSwitcher && this.$refs.planSwitcher.fetchPlans();

          this.closeUpsertPlanModal();
          this.$snackbar &&
            this.$snackbar.success &&
            this.$snackbar.success("Plan added");
        }
      );
    },
    editPlan() {
      // Only continue if the plan name has changed
      if (this.planToEdit.displayName === this.planName) {
        this.closeUpsertPlanModal();
        return;
      }
      this.upsertingPlan = true;
      this.planToEdit.displayName = this.planName;

      this.planToEdit.update((plan, err) => {
        this.upsertingPlan = false;
        if (err || !plan) {
          this.$snackbar.show(err || "Failed to update plan.");
          return;
        }
        // Refresh the switcher list so the updated plan appears in the menu
        this.$refs.planSwitcher.fetchPlans();
        this.$snackbar.show("Changes saved");
        this.closeUpsertPlanModal();
      });
    },
  },

  data: () => ({
    sideMenuIsExpanded: false,
    initiallyExpanded: false,
    showUpsertPlanModal: false,
    planName: "",
    planToEdit: null,
    upsertingPlan: false,
    isUpsertPlanFormValid: true,
  }),
  computed: {
    accounts: function () {
      // The tier list is non-empty only below the anchor, which is exactly where
      // `masterAccounts` cannot name the account: it holds the user's own memberships, and those
      // stop at the anchor.
      const currentTierAccounts = this.$store.getters.currentTierAccounts;
      if (currentTierAccounts.length) return currentTierAccounts;

      var accounts = [];

      if (
        this.$store.state.authenticatedUser &&
        this.$store.state.authenticatedUser.masterAccounts
      ) {
        for (
          var i = 0;
          i < this.$store.state.authenticatedUser.masterAccounts.length;
          i++
        ) {
          var masterAccount =
            this.$store.state.authenticatedUser.masterAccounts[i];

          accounts.push({
            id: masterAccount.id,
            displayName: masterAccount.displayName,
          });
        }
      }

      if (accounts.length) return accounts;

      // A NextLevel user has no client accounts -- their membership is an AccountUser row on the
      // anchor -- so without this the switcher renders its box with nothing selected.
      const anchorAccount = this.$store.getters.anchorAccount;

      return anchorAccount
        ? [{ id: anchorAccount.uuid, displayName: anchorAccount.displayName }]
        : [];
    },
    // PcAdvisorLayout renders the bar in its short form; the tall banner variant would have to
    // be said here too.
    topBarHeight: function () {
      return TOP_BAR_HEIGHT;
    },
    isBelowAnchor: function () {
      return isBelowAnchor(
        this.$store.getters.anchorAccount,
        this.$store.state.masterAccount
      );
    },
    // The tier selector is drawn below the anchor and only with more than one account to choose
    // from: with a single one there was no choice to make, so the switcher names it instead.
    showsTierSelector: function () {
      return (
        this.isBelowAnchor && this.$store.getters.currentTierAccounts.length > 1
      );
    },
    selectedAccountId() {
      return this.$store.state.masterAccount?.id;
    },
    selectedPlanId: {
      get() {
        return this.$store.state.masterAccount
          ? this.$store.state.masterAccount.selectedPlanId
          : null;
      },
      set(v) {
        this.$store.commit("setMasterAccountSelectedPlanId", v);
      },
    },
    accountId() {
      return (
        this.$store.state.masterAccount && this.$store.state.masterAccount.id
      );
    },
    planNameRequired() {
      return (v) => (v && v.trim().length > 0) || "Please enter a plan name";
    },
    planNameValid() {
      if (!this.$refs.planSwitcher?.plans) return true;

      if (this.planName && this.planName.length < 5) {
        return "Plan name must be at least 5 characters";
      }

      const plans = this.$refs.planSwitcher?.plans || [];

      // Ignore case sensitivity for usability
      const isSameName = (a, b) =>
        (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

      // Reuse conflict logic for both edit and add
      const conflict = plans.some(
        (p) =>
          isSameName(p.displayName, this.planName) &&
          (!this.planToEdit || p.id !== this.planToEdit.id)
      );
      return conflict ? "A plan with this name already exists." : true;
    },
  },

  mounted: function () {
    this.sideMenuIsExpanded = !this.collapsed;
    this.initiallyExpanded = this.sideMenuIsExpanded;
  },
};
</script>

<style lang="scss" scoped>
:deep(#cancelButton),
:deep(#submitButton) {
  text-transform: unset;
  width: 128px;
  padding: 8px 24px 8px 24px;
  border-radius: 4px;
  border-color: $penChecksTeal !important;
  font-family: Open Sans;
  font-size: 14px !important;
  font-style: normal;
  font-weight: 500;
  line-height: normal !important;
}
:deep(#submitButton) {
  background-color: $penChecksTeal !important;
}
:deep(.pc-modal-card .v-card__text) {
  padding: 24px 32px !important;
}
:deep(.pc-modal-actions) {
  margin: 0 !important;
}
:deep(.v-card__actions) {
  padding: 0 !important;
}
:deep(.modal-title) {
  color: $penChecksDark;
  font-family: Open Sans !important;
  font-size: 18px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 140% !important; /* 25.2px */
  letter-spacing: 0.027px !important;
}
// The drawer sits below the portal's navigation bar when there is one.
//
// Not Vuetify's `clipped`, which offsets by the sum of every mounted app bar. The sticky page
// header in PcFixedHeaderLayout is one of those, but it sits inside the content area to the RIGHT
// of this drawer -- summing it dropped the drawer by the height of each page's header. Only the
// portal bar is actually above the drawer.
.advisor-side-nav--below-bar {
  :deep(.v-navigation-drawer) {
    top: var(--advisor-top-bar-height) !important;
    max-height: calc(100% - var(--advisor-top-bar-height)) !important;
  }
}

// The account switcher re-drawn to the design, from here rather than in the shared component so
// every other portal keeps it as it is. These replace Vuetify's solo-dense metrics outright rather
// than nudging them, which is what the !important on each is for.
.account-switcher {
  :deep(.v-input__prepend-inner) {
    align-self: center;
    margin-top: 0 !important;
    padding-right: 0 !important;
  }

  :deep(.pc-account-switcher-avatar) {
    // The gap to the name; the slot owns the vertical padding.
    padding: 0 8px 0 0 !important;
  }

  :deep(.pc-account-switcher-letter) {
    // primary/dark teal in the design's palette -- a different value from $penChecksDarkTeal.
    color: #2b616f !important;
  }
}

// Dark, against the top of the window: what a user with a single tier sees.
.account-switcher--default {
  :deep(.v-input__slot) {
    min-height: 46px !important;
    padding: 7px 8px !important;
    // Vuetify writes the component's own background inline, through its background-color prop.
    background-color: #71849b !important;
  }
}

// White, under the top navigation bar, which has taken the dark treatment. Taller than the dark
// variant: 12px of vertical padding against 7px.
.account-switcher--secondary {
  border-bottom: 1px solid $penChecksLightGray;

  :deep(.v-input__slot) {
    min-height: 56px !important;
    padding: 12px 8px !important;
    background-color: $white !important;
  }

  :deep(.pc-account-switcher-avatar .v-avatar) {
    // brand/lowest-contrast, over Vuetify's `white` colour class -- which carries !important of
    // its own, so this wins on specificity rather than on importance.
    background-color: #ebf3f5 !important;
  }

  :deep(.v-select__selection) {
    color: $penChecksDark !important;
  }

  :deep(.pc-account-switcher-chevron) {
    color: $penChecksDarkGray !important;
  }
}

#advisor-side-nav {
  :deep(#side-nav-top) {
    height: 48px;
    background-color: $penChecksBlueGray;
    font-size: 16px;
    color: $white;
    padding: 12px;

    // Prevent tap text-selection
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    .initials {
      color: $penChecksNavyBlue;
      font-weight: bold;
    }

    :deep(#account-name) {
      position: absolute;
      transition: opacity 0.3s;
      white-space: nowrap;
      text-overflow: ellipsis;
      line-height: 27px;
      max-width: 190px;
      overflow: hidden;
      cursor: default;
    }
  }

  :deep(.v-navigation-drawer__border) {
    display: none;
  }

  // Vuetify's hover background tint derives from currentColor, and the shared
  // PcClientSideNavigationItem separately hardcodes the icon and title text
  // color via its own `!important` rule. Both the row's state-overlay AND the
  // icon/title text need to be overridden for hover to visibly render teal.
  // Scoped to the advisor portal only.
  //
  // Hover only, and deliberately not `.v-list-item--active`. Which item is
  // current is already said by the `selected` prop, and Vuetify's own active
  // state answers a different question: it matches the item's `to` against the
  // route inclusively, so the first item of every menu -- whose `to` is the
  // account itself, a prefix of every sibling's -- would read as current on
  // every page under that account and never return to grey.
  :deep(.side-nav-item .v-list-item:hover) {
    color: $penChecksTeal !important;
  }

  :deep(.side-nav-item .v-list-item:hover .side-nav-item-content) {
    color: $penChecksTeal !important;
  }
}
</style>
